import Vue from 'vue';
import Fragment from 'vue-fragment';
import _defaults from 'lodash/defaults';
Vue.use(Fragment.Plugin);

const defaultVueSettings: Partial<VueSettings> = {
    context: 'app',
    mountNode: '#app',
    useBuefy: true,
};

export class VueSettings {
    public context: string;
    public mountNode: string
    public useBuefy: boolean

    public constructor (settings?: Partial<VueSettings>) {
        _defaults(this, settings, defaultVueSettings);
    }
}

/*
    Global Component Registration
    https://vuejs.org/v2/guide/components-registration.html#Automatic-Global-Registration-of-Base-Components
*/
const requireComponent = require.context(
    // The relative path of the components folder
    '@/vue/components/base',
    // Whether or not to look in subfolders
    true,
    // The regular expression used to match base component filenames
    /.(vue|js)$/
);

requireComponent.keys().forEach((fileName) => {
    /* 
        components may be either in the root folder or in a 
        subfolder matching the component name 
            e.g. /base/BaseRadio.vue 
            or 
            /base/BaseRadio/BaseRadio.vue
    */

    // Get component config
    const config = requireComponent(fileName);

    //  Look for the component options on `.default`, which will
    //  exist if the component was exported with `export default`,
    //  otherwise fall back to module's root.
    const componentConfig = config.default || config;

    //clean the filename to use as the component name
    const componentName = fileName
        .split('/')
        .pop()
        .replace(/\.\w+$/, '');

    // Register component on the Vue instance
    Vue.component(componentName, componentConfig);
});

const loadAppContext = async (vueSettings: Partial<VueSettings>) => {


    const plugins = await Promise.all([
        import(/* webpackChunkName: "portal-vue" */ 'portal-vue'),
        import(
            /* webpackChunkName: "translatePlugin" */ '@/vue/plugins/translatePlugin'
        ),
        import(
            /* webpackChunkName: "domPurifyPlugin" */ '@/vue/plugins/domPurifyPlugin'
        ),
    ]);

    if (vueSettings.useBuefy) {
        plugins.push(await import(/* webpackChunkName: "buefy" */ 'buefy'));
    }

    // attach plugins
    plugins.forEach(function(plugin) {
        Vue.use(plugin.default);
    });

    // make base components globally available
    // _registerBaseComponents();

    // instance Settings
    return {
    };
};

const getVueConfiguration = async (settings: Partial<VueSettings> = {}) => {
    const vueSettings = new VueSettings(settings);
    let config = {};

    if (vueSettings.context === 'app') {
        config = await loadAppContext(vueSettings);
    }

    return {
        el: vueSettings.mountNode,
        ...config,
    };
};

export default async (component: object, componentOptions: object, settings: Partial<VueSettings>) => {
    /*
        1) Creates a new Vue instance, 
        2) renders a single file component as its template, 
        3) mounts the instance when the DOM is ready
        
        @componentOptions is an instance of Vue componentOptions
        https://vuejs.org/v2/api/#componentOptions-Data
    */
    
    const config = await getVueConfiguration(settings);

    // render view
    return new Vue({
        ...config,
        render(h) {
            return h(component, componentOptions);
        },
    } as unknown);
};
